import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MessageService } from 'primeng/api';
import { MerchantService, NationalityService, MerchantUserService } from 'app/services';
import { Salutation, Suffix, CompanyPosition, MerchantStatusEnum } from 'app/models';
import { MerchantConfirmOnboardingComponent } from 'app/components/merchant/merchant-confirm-onboarding/merchant-confirm-onboarding.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OnboardingPartner } from '../../../../models/merchant/onboarding-partner';
import { debounceTime, Observable, of, switchMap } from 'rxjs';

@Component({
    selector: 'merchant-information-confirm',
    templateUrl: './merchant-information-confirm.component.html',
    styleUrls: ['./merchant-information-confirm.component.scss',
        '../../../../../styles/sidebar.scss',
        '../../../../../styles/style-layout.scss',
        '../../../../../styles/default-material-theme.scss'],
    providers: [MessageService]
})
export class MerchantInformationConfirmComponent implements OnInit {
    public showMerchantInformationConfirmSideBar: boolean = false;
    @Input()
    set _showMerchantInformationConfirmSideBar(value: boolean) {
        this.showMerchantInformationConfirmSideBar = value;
    }

    @Input() public merchantConfirmOnboardingComponent!: MerchantConfirmOnboardingComponent;

    @Output() changedShowMerchantInformationConfirmSideBarEmit = new EventEmitter<boolean>();
    changedShowMerchantInformationConfirmSideBar(increased: any) {
        this.changedShowMerchantInformationConfirmSideBarEmit.emit(increased);
    }

    public merchant!: OnboardingPartner;
    @Input()
    set _merchant(obj: any) {
        this.merchant = obj;
        this.initializePartnerForm();
    }

    @Output() confirmMerchant = new EventEmitter<MerchantStatusEnum>();

    constructor(private messageService: MessageService, private merchantUserService: MerchantUserService,
        private merchantService: MerchantService, private nationalityService: NationalityService, private formBuilder: FormBuilder,) { }

    public companyPositions = CompanyPosition.CompanyPositionList;
    public salutations = Salutation.SalutationList;
    public suffixes = Suffix.SuffixList;

    public nationalities: any = [];

    public loading: boolean = false;
    partnerDetailsForm: FormGroup = new FormGroup({});
    public isEditable: boolean = false;
    merchantStatusEnum = MerchantStatusEnum;
    merchantValue!: OnboardingPartner;
    public addresses: any[] = [];


    ngOnInit(): void {
        this.getNationalities();
    }

    private initializePartnerForm(): void {
        this.partnerDetailsForm = this.formBuilder.group({
          CompanyPosition: [this.merchant.CompanyPosition, [Validators.required]],
          Salutation: [this.merchant.Salutation, [Validators.required]],
          FirstName: [this.merchant.FirstName, [Validators.required]],
          LastName: [this.merchant.LastName, [Validators.required]],
          Suffix: [this.merchant.Suffix],
          Address: [this.merchant.Address, [Validators.required]],
          City: [this.merchant.City, [Validators.required]],
          PostalCode: [this.merchant.PostalCode, [Validators.required, Validators.pattern(/^\d{4}$/)]],
          NationalityId: [this.merchant.NationalityId, [Validators.required]],
          Email: [this.merchant.Email, [Validators.required, Validators.email]],
          Birthday: [this.merchant.Birthday, [Validators.required]],
          Phone: [this.merchant.Phone, [Validators.required, Validators.pattern(/^(09|\+639)\d{9}$/)]]
        });
        this.partnerDetailsForm.get('CompanyPosition')?.disable();
        this.partnerDetailsForm.get('Salutation')?.disable();
        this.partnerDetailsForm.get('FirstName')?.disable();
        this.partnerDetailsForm.get('LastName')?.disable();
        this.partnerDetailsForm.get('Suffix')?.disable();
        this.partnerDetailsForm.get('Address')?.disable();
        this.partnerDetailsForm.get('City')?.disable();
        this.partnerDetailsForm.get('PostalCode')?.disable();
        this.partnerDetailsForm.get('NationalityId')?.disable();
        this.partnerDetailsForm.get('Email')?.disable();
        this.partnerDetailsForm.get('Birthday')?.disable();
        this.partnerDetailsForm.get('Phone')?.disable();
        this.merchantValue = this.partnerDetailsForm.value;
        this.partnerDetailsForm.controls['Address'].valueChanges.pipe(
            debounceTime(300),
            switchMap((query: string) => (query ? this.search(query) : of([])))
        ).subscribe((results: any) => {
            this.addresses = results;
        });
    }

    public getNationalities() {
        this.nationalityService.getIsActiveNationalities(null, this.messageService).then(data => {
            if (!data) {
                return;
            }

            if (!data.Success) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.nationalities = data.Items;
        });
    }

    public confirm() {
        this.loading = true;

        const dto = {
            MerchantId: this.merchant.MerchantId,
            MerchantUserId: this.merchant.MerchantUserId,
            CompanyPosition: this.partnerDetailsForm.value.CompanyPosition,
            Salutation: this.partnerDetailsForm.value.Salutation,
            FirstName: this.partnerDetailsForm.value.FirstName,
            LastName: this.partnerDetailsForm.value.LastName,
            Suffix: this.partnerDetailsForm.value.Suffix,
            Address: this.partnerDetailsForm.value.Address,
            City: this.partnerDetailsForm.value.City,
            PostalCode: this.partnerDetailsForm.value.PostalCode,
            Birthday: this.partnerDetailsForm.value.Birthday,
            NationalityId: this.partnerDetailsForm.value.NationalityId,
            Phone: this.partnerDetailsForm.value.Phone,
            Email: this.partnerDetailsForm.value.Email,
        };

        if (!dto.CompanyPosition || !dto.Salutation || !dto.FirstName || !dto.LastName || !dto.Birthday || !dto.Phone || !dto.Email) {
            this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Fill all required fields.' });
            this.loading = false;
            return;
        }

        this.merchantUserService.merchantUserUpdate(dto, this.messageService).then(data => {
            if (!data) {
                this.loading = false;
                return;
            }

            if (!data.Success) {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                return;
            }
            this.merchantConfirmOnboardingComponent.getMerchantUserAndMerchantForConfirmed();
            this.messageService.add({ severity: 'success', summary: 'Details updated successfully.', detail: '' });
        });
    }

    cancel() {
        this.changedShowMerchantInformationConfirmSideBar(this.showMerchantInformationConfirmSideBar);
    }

    public enableFields() {
        this.isEditable = true;
        this.partnerDetailsForm.get('CompanyPosition')?.enable();
        this.partnerDetailsForm.get('Salutation')?.enable();
        this.partnerDetailsForm.get('FirstName')?.enable();
        this.partnerDetailsForm.get('LastName')?.enable();
        this.partnerDetailsForm.get('Suffix')?.enable();
        this.partnerDetailsForm.get('Address')?.enable();
        this.partnerDetailsForm.get('City')?.enable();
        this.partnerDetailsForm.get('PostalCode')?.enable();
        this.partnerDetailsForm.get('NationalityId')?.enable();
        this.partnerDetailsForm.get('Email')?.enable();
        this.partnerDetailsForm.get('Birthday')?.enable();
        this.partnerDetailsForm.get('Phone')?.enable();
    }

    public disableFields() {
        this.isEditable = false;
        this.partnerDetailsForm.get('CompanyPosition')?.disable();
        this.partnerDetailsForm.get('Salutation')?.disable();
        this.partnerDetailsForm.get('FirstName')?.disable();
        this.partnerDetailsForm.get('LastName')?.disable();
        this.partnerDetailsForm.get('Suffix')?.disable();
        this.partnerDetailsForm.get('Address')?.disable();
        this.partnerDetailsForm.get('City')?.disable();
        this.partnerDetailsForm.get('PostalCode')?.disable();
        this.partnerDetailsForm.get('NationalityId')?.disable();
        this.partnerDetailsForm.get('Email')?.disable();
        this.partnerDetailsForm.get('Birthday')?.disable();
        this.partnerDetailsForm.get('Phone')?.disable();
        this.partnerDetailsForm.setValue(this.merchantValue);
    }

    onConfirmMerchant(){
        this.confirmMerchant.emit(this.merchantStatusEnum.ApprovedAuthorizedRepresentative);
    }

    //Google maps autocomplete
    public search(event: any) {
        return new Observable((observer) => {
            const query = event;
            const service = new google.maps.places.AutocompleteService();
            service.getPlacePredictions({ input: query, componentRestrictions: { country: 'PH' } }, (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    observer.next(predictions ? predictions.map((prediction) => prediction.description) : []);
                } else {
                    observer.next([]);
                }
            });
        });
    }
}
